import './rr-featured-compare.js?v=20261003-collection-navigation-2';

export const validCollectionIndex = (value, count) => Number.isInteger(value) && value >= 0 && value < count;

export class CollectionNavigation extends (globalThis.HTMLElement || class {}) {
    connectedCallback() {
      if (this.abort) return;
      this.abort = new AbortController();
      this.animations = new Set();
      this.opening = false;
      this.switching = false;
      this.pendingIndex = undefined;
      this.reduced = matchMedia('(prefers-reduced-motion: reduce)');
      this.mobile = matchMedia('(max-width: 699px)');
      this.modeVersion = 0;
      this.overview = this.querySelector('.rr-collection-overview');
      this.choices = this.querySelector('[data-collection-choices]');
      this.products = this.querySelector(':scope > featured-collections-carousel');
      this.navigation = this.querySelector('.featured-collections-navigation');
      this.buttons = [...this.navigation.querySelectorAll('button')];
      this.products.inert = !this.mobile.matches && this.dataset.stage !== 'products';
      const listen = (target, event, callback, options = {}) => target.addEventListener(event, callback, { ...options, signal: this.abort.signal });
      listen(this.mobile, 'change', () => this.syncMobileMode());
      this.syncMobileMode();
      for (const event of ['wheel', 'touchstart']) listen(window, event, () => this.scrollAnimation?.cancel(), { passive: true });
      listen(document, 'keydown', event => {
        if (['ArrowUp', 'ArrowDown', 'PageUp', 'PageDown', 'Home', 'End', ' '].includes(event.key)) this.scrollAnimation?.cancel();
      });
      listen(this, 'click', event => {
        if (this.mobile.matches) return;
        const choice = event.target.closest('[data-rr-collection-index]');
        if (!choice || !event.target.closest('a') || event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
        event.preventDefault();
        this.open(Number(choice.dataset.rrCollectionIndex), event.detail === 0);
      });
      listen(this.navigation, 'click', event => {
        if (this.mobile.matches) return;
        const index = this.buttons.indexOf(event.target.closest('button'));
        if (index < 0) return;
        event.preventDefault();
        event.stopImmediatePropagation();
        this.switchCollection(index);
      }, { capture: true });
      listen(this, 'shopify:block:select', event => {
        if (this.mobile.matches) return;
        const choice = event.target.closest('[data-rr-collection-index]');
        const index = choice ? Number(choice.dataset.rrCollectionIndex) : [...this.products.children].indexOf(event.target);
        if (!validCollectionIndex(index, this.buttons.length)) return;
        event.stopPropagation();
        if (this.dataset.stage === 'collections') this.choices.move?.(index - this.choices.index, true);
        else this.switchCollection(index);
        this.scrollIntoView({ block: 'center', behavior: this.reduced.matches ? 'auto' : 'smooth' });
      }, { capture: true });
      listen(this.reduced, 'change', () => {
        if (this.reduced.matches) for (const animation of this.animations) animation.finish();
      });
      customElements.whenDefined('featured-collections-carousel').then(() => {
        if (!this.abort || !this.isConnected) return;
        this.originalTransition = this.products.createOnChangeAnimationControls;
        this.products.createOnChangeAnimationControls = (from, to) => ({
          leaveControls: () => this.transition(from, { opacity: [1, 0], transform: ['translateY(0)', 'translateY(-8px)'] }, 220),
          enterControls: () => this.transition(to, { opacity: [0, 1], transform: ['translateY(12px)', 'translateY(0)'] }, 380)
        });
      });
    }

    disconnectedCallback() {
      this.abort?.abort();
      this.abort = null;
      for (const animation of this.animations) animation.cancel();
      this.animations.clear();
      if (this.originalTransition) this.products.createOnChangeAnimationControls = this.originalTransition;
      this.style.removeProperty('height');
      this.style.removeProperty('overflow');
      this.products.style.removeProperty('opacity');
      this.navigation.style.removeProperty('opacity');
      for (const property of ['position', 'top', 'inset-inline', 'width']) this.overview.style.removeProperty(property);
    }

    syncMobileMode() {
      this.modeVersion++;
      for (const animation of this.animations) animation.cancel();
      this.animations.clear();
      if (this.dataset.stage === 'opening') this.dataset.stage = 'collections';
      this.opening = false;
      this.switching = false;
      this.pendingIndex = undefined;
      this.removeAttribute('aria-busy');
      this.style.removeProperty('height');
      this.style.removeProperty('overflow');
      this.products.style.removeProperty('opacity');
      this.navigation.style.removeProperty('opacity');
      for (const property of ['position', 'top', 'inset-inline', 'width']) this.overview.style.removeProperty(property);
      this.overview.inert = this.mobile.matches || this.dataset.stage === 'products';
      this.products.inert = !this.mobile.matches && this.dataset.stage !== 'products';
      if (this.mobile.matches) {
        this.choices.cancel?.();
        clearTimeout(this.choices.timer);
      }
    }

    transition(element, keyframes, duration, delay = 0, hold = false) {
      const animation = element.animate(keyframes, {
        duration: this.reduced.matches ? 0 : duration,
        delay: this.reduced.matches ? 0 : delay,
        easing: 'cubic-bezier(.22, 1, .36, 1)', fill: 'both'
      });
      this.animations.add(animation);
      const cancel = () => {
        this.animations.delete(animation);
        animation.cancel();
      };
      const finished = animation.finished.catch(() => {}).then(() => {
        if (!hold) cancel();
      });
      return { finished, finish: () => animation.finish(), cancel };
    }

    scrollTitleIntoView() {
      const heading = this.firstElementChild?.querySelector('p, h2');
      if (!heading) return;
      const headerHeight = parseFloat(getComputedStyle(document.documentElement).getPropertyValue('--header-height')) || 0;
      const headingStyle = getComputedStyle(heading);
      const leading = Math.max(0, (parseFloat(headingStyle.lineHeight) - parseFloat(headingStyle.fontSize)) / 2) || 0;
      this.scrollAnimation?.cancel();
      const from = window.scrollY;
      const to = Math.max(0, from + heading.getBoundingClientRect().top + leading - headerHeight - 32);
      if (this.reduced.matches || Math.abs(to - from) < 1) {
        window.scrollTo({ top: to, behavior: 'instant' });
        return;
      }
      let frame, started;
      const control = {
        cancel: () => {
          cancelAnimationFrame(frame);
          this.animations.delete(control);
          if (this.scrollAnimation === control) this.scrollAnimation = null;
        },
        finish: () => { window.scrollTo({ top: to, behavior: 'instant' }); control.cancel(); }
      };
      const step = timestamp => {
        started ??= timestamp;
        const progress = Math.min(1, (timestamp - started) / 1000);
        const eased = (1 - Math.cos(Math.PI * progress)) / 2;
        window.scrollTo({ top: from + (to - from) * eased, behavior: 'instant' });
        if (progress < 1) frame = requestAnimationFrame(step);
        else control.cancel();
      };
      this.scrollAnimation = control;
      this.animations.add(control);
      frame = requestAnimationFrame(step);
    }

    async open(index, keyboard = false) {
      if (this.mobile?.matches || !validCollectionIndex(index, this.buttons.length) || this.opening || this.dataset.stage === 'products') return;
      const modeVersion = this.modeVersion;
      this.opening = true;
      this.setAttribute('aria-busy', 'true');
      this.overview.inert = true;
      this.choices.cancel?.();
      clearTimeout(this.choices.timer);
      this.scrollTitleIntoView();
      const bounds = this.getBoundingClientRect();
      const height = bounds.height;
      const overviewTop = this.overview.getBoundingClientRect().top - bounds.top;
      // Hold the outgoing cards in place while the incoming gallery measures.
      // The heading remains in normal flow and is never animated.
      this.style.height = `${height}px`;
      this.style.overflow = 'clip';
      this.overview.style.position = 'absolute';
      this.overview.style.top = `${overviewTop}px`;
      this.overview.style.setProperty('inset-inline', '0');
      this.overview.style.width = '100%';
      this.products.style.opacity = '0';
      this.navigation.style.opacity = '0';
      this.dataset.stage = 'opening';
      await customElements.whenDefined('featured-collections-carousel');
      if (!this.abort || this.modeVersion !== modeVersion) return;
      await this.products.select(index, { instant: true });
      if (!this.abort || this.modeVersion !== modeVersion) return;
      this.navigation.onNavigationChange(index);
      this.products.inert = true;
      await new Promise(resolve => requestAnimationFrame(() => requestAnimationFrame(resolve)));
      if (!this.abort || this.modeVersion !== modeVersion) return;
      this.style.height = 'auto';
      const targetHeight = this.getBoundingClientRect().height;
      this.style.height = `${height}px`;
      const changes = [
        this.transition(this, { height: [`${height}px`, `${targetHeight}px`] }, 480, 0, true),
        this.transition(this.overview, { opacity: [1, 0] }, 460, 0, true),
        this.transition(this.navigation, { opacity: [0, 1] }, 460, 0, true),
        this.transition(this.products, { opacity: [0, 1] }, 460, 0, true)
      ];
      if (keyboard || this.overview.contains(document.activeElement)) this.buttons[index].focus({ preventScroll: true });
      await Promise.all(changes.map(change => change.finished));
      if (!this.abort || this.modeVersion !== modeVersion) return;
      this.dataset.stage = 'products';
      this.products.style.removeProperty('opacity');
      this.navigation.style.removeProperty('opacity');
      for (const property of ['position', 'top', 'inset-inline', 'width']) this.overview.style.removeProperty(property);
      this.style.removeProperty('height');
      this.style.removeProperty('overflow');
      for (const change of changes) change.cancel();
      this.products.inert = false;
      this.removeAttribute('aria-busy');
      this.opening = false;
      if (this.pendingIndex !== undefined) this.switchCollection(this.pendingIndex);
    }

    async switchCollection(index) {
      if (this.mobile?.matches || !validCollectionIndex(index, this.buttons.length)) return;
      const modeVersion = this.modeVersion;
      this.pendingIndex = index;
      if (this.switching || this.opening) return;
      this.switching = true;
      this.products.inert = true;
      while (this.pendingIndex !== undefined && this.abort && this.modeVersion === modeVersion) {
        const next = this.pendingIndex;
        this.pendingIndex = undefined;
        this.navigation.onNavigationChange(next);
        await this.products.select(next);
      }
      if (!this.abort || this.modeVersion !== modeVersion) return;
      this.products.inert = false;
      this.switching = false;
    }
}

if (typeof customElements !== 'undefined' && !customElements.get('rr-collection-navigation')) {
  customElements.define('rr-collection-navigation', CollectionNavigation);
}
